/**
 * @file The live consultation chat over the consult room's Socket.IO connection.
 * @module hooks/useConsultChat
 *
 * S4 of ClickUp 86d42x0pt. Mounted by both consult surfaces; the panel that renders it knows
 * nothing about the socket.
 *
 * ⚠️ NO NOTIFICATION SERVICE. This chat is talk + Redis, deliberately: the patient telehealth page
 * is unauthenticated, so the notification service's conversation model has nobody to hang it on.
 * Do not reach into `api/messaging` or `components/messaging` from here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ConsultChatAttachment, ConsultChatMessage } from "../../types/appointments/ConsultChatTypes";

/** talk's own cap (`server.js::CHAT_CONTENT_MAX_LENGTH`) — mirrored so a refusal costs no round trip. */
export const CHAT_CONTENT_MAX_LENGTH = 4000;

/** How long the hook keeps looking for the page's socket before giving up. Bounded, never a spin. */
const SOCKET_LOOKUP_INTERVAL_MS = 300;
const SOCKET_LOOKUP_ATTEMPTS = 20;

/** Structural, not `socket.io-client`'s `Socket`: the pages hold it as `any` off the singleton. */
export interface ConsultChatSocket {
	connected?: boolean;
	on: (event: string, handler: (payload?: any) => void) => void;
	off: (event: string, handler?: (payload?: any) => void) => void;
	emit: (event: string, payload?: any) => void;
}

export interface UseConsultChatOptions {
	roomId: string;
	/** The page's accessor. ⚠️ The hook never reads a cookie or touches SocketService itself. */
	getSocket: () => ConsultChatSocket | null;
	enabled: boolean;
	/** Display metadata only — talk trims it, caps it at 120 chars and forces it null for a patient. */
	senderName?: string | null;
	/** Own-role arrivals do not count as unread. Optional: without it every arrival counts. */
	selfRole?: ConsultChatMessage["senderRole"];
	/** Injected so the hook stays ignorant of which half of the consult it is running in. */
	uploadAttachment?: (file: File) => Promise<ConsultChatAttachment[]>;
}

export interface ConsultChatFeed {
	messages: ConsultChatMessage[];
	/** `false` when refused client-side or when the socket is down — nothing is emitted either way. */
	sendMessage: (content: string) => boolean;
	/** Uploads first; emits only on success, so a message can never reference a file that is not there. */
	sendAttachment: (file: File) => Promise<boolean>;
	unreadCount: number;
	markRead: () => void;
	isReady: boolean;
	error: string | null;
	dismissError: () => void;
}

/** ⚠️ An ALLOW-LIST — a vocabulary the server owns is never echoed back unrecognised. */
const REJECTION_MESSAGES: Record<string, string> = {
	rate_limited: "You are sending messages too quickly. Wait a moment and try again.",
	content_empty: "Type a message before sending.",
	content_too_long: `That message is too long. Keep it under ${CHAT_CONTENT_MAX_LENGTH} characters.`,
	attachments_too_many: "Too many attachments on one message.",
	attachment_invalid: "That attachment could not be sent.",
	attachments_invalid: "That attachment could not be sent.",
	sender_role_not_allowed: "You cannot send messages in this consultation.",
};

const GENERIC_REJECTION = "That message could not be sent.";
const NOT_CONNECTED = "The consultation chat is not connected. Your message was not sent.";
const NOT_AUTHORISED = "The chat is not available for this consultation.";
const GENERIC_ATTACHMENT_ERROR = "The file could not be uploaded. Please try again.";
const ATTACHMENTS_UNAVAILABLE = "Attachments are not available in this consultation.";

const messageForRejection = (reason: unknown): string => {
	const code = typeof reason === "string" ? reason : "";
	return (code && Object.prototype.hasOwnProperty.call(REJECTION_MESSAGES, code) ? REJECTION_MESSAGES[code] : "") || GENERIC_REJECTION;
};

/** Only our own upload error carries a sentence written for a patient to read. */
const messageForUploadError = (error: unknown): string => {
	const named = error as { name?: unknown; message?: unknown };
	return named?.name === "ConsultChatUploadError" && typeof named.message === "string" && named.message ? named.message : GENERIC_ATTACHMENT_ERROR;
};

const toAttachment = (value: unknown): ConsultChatAttachment | null => {
	if (!value || typeof value !== "object") return null;

	const raw = value as Record<string, unknown>;
	const name = typeof raw.name === "string" ? raw.name.trim() : "";
	if (!name) return null;

	return {
		id: typeof raw.id === "string" ? raw.id : "",
		name,
		mime: typeof raw.mime === "string" ? raw.mime : "",
		size: typeof raw.size === "number" && Number.isFinite(raw.size) ? raw.size : 0,
	};
};

/** One wire message → a usable one, or `null` if it is not one. The id is the dedupe key, so it is required. */
export const toChatMessage = (value: unknown): ConsultChatMessage | null => {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;

	const raw = value as Record<string, unknown>;
	const id = typeof raw.id === "string" ? raw.id.trim() : "";
	if (!id) return null;
	if (raw.senderRole !== "practitioner" && raw.senderRole !== "patient") return null;
	if (raw.type !== "text" && raw.type !== "file") return null;
	if (typeof raw.content !== "string") return null;

	const senderName = typeof raw.senderName === "string" ? raw.senderName.trim() : "";

	return {
		id,
		senderRole: raw.senderRole,
		senderUserId: typeof raw.senderUserId === "string" && raw.senderUserId ? raw.senderUserId : null,
		senderName: senderName || null,
		type: raw.type,
		content: raw.content,
		attachments: Array.isArray(raw.attachments) ? raw.attachments.map(toAttachment).filter((a): a is ConsultChatAttachment => a !== null) : [],
		timestamp: typeof raw.timestamp === "number" && Number.isFinite(raw.timestamp) ? raw.timestamp : 0,
	};
};

/** A `chatHistory` reply's `messages` → the usable ones. At most two JSON unwraps; never throws. */
export const toChatMessages = (value: unknown): ConsultChatMessage[] => {
	let candidate: unknown = value;

	for (let depth = 0; depth < 2 && typeof candidate === "string"; depth += 1) {
		const trimmed = candidate.trim();
		if (!trimmed) return [];

		try {
			candidate = JSON.parse(trimmed);
		} catch {
			return [];
		}
	}

	if (!Array.isArray(candidate)) return [];

	return candidate.map(toChatMessage).filter((m): m is ConsultChatMessage => m !== null);
};

const asRecord = (payload: unknown): Record<string, unknown> => (payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {});

/**
 * The chat for one consultation room. Keyed by message id, so the sender's own broadcast echo, a
 * live arrival and a history replay of one message collapse to a single entry.
 */
export const useConsultChat = ({ roomId, getSocket, enabled, senderName, selfRole, uploadAttachment }: UseConsultChatOptions): ConsultChatFeed => {
	const [byId, setById] = useState<Map<string, ConsultChatMessage>>(() => new Map());
	const [unreadCount, setUnreadCount] = useState(0);
	const [connected, setConnected] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const socketRef = useRef<ConsultChatSocket | null>(null);
	// One request per connection, reset by a disconnect and by a refused read so `roomInfo` retries.
	const historyRequestedRef = useRef(false);

	// Read through refs so a fresh arrow or a changed name never re-subscribes the socket.
	const getSocketRef = useRef(getSocket);
	getSocketRef.current = getSocket;
	const uploadRef = useRef(uploadAttachment);
	uploadRef.current = uploadAttachment;
	const senderNameRef = useRef(senderName);
	senderNameRef.current = senderName;
	const selfRoleRef = useRef(selfRole);
	selfRoleRef.current = selfRole;

	useEffect(() => {
		if (!enabled || !roomId) return undefined;

		let cancelled = false;
		let lookupTimer: ReturnType<typeof setTimeout> | null = null;
		let attempts = 0;
		let attached: ConsultChatSocket | null = null;

		const requestHistory = () => {
			const socket = socketRef.current;
			if (!socket || !socket.connected || historyRequestedRef.current) return;

			historyRequestedRef.current = true;
			// ⚠️ THE BARE STRING is the whole first argument — `requestChatHistory` takes no object.
			socket.emit("requestChatHistory", roomId);
		};

		const handlers: Array<[string, (payload?: any) => void]> = [
			[
				"connect",
				() => {
					if (cancelled) return;
					setConnected(true);
					historyRequestedRef.current = false;
					// SocketService's own `connect` handler is registered first and emits `joinRoom`, so this is strictly after the page's join.
					requestHistory();
				},
			],
			[
				"disconnect",
				() => {
					if (cancelled) return;
					setConnected(false);
					historyRequestedRef.current = false;
				},
			],
			[
				// The definitive grant signal: talk emits `roomInfo` only after `joinRoom` passed `canAccessRoom`.
				"roomInfo",
				(payload?: any) => {
					if (cancelled) return;

					const inbound = asRecord(payload).roomId;
					if (typeof inbound === "string" && inbound !== roomId) return;

					setConnected(true);
					requestHistory();
				},
			],
			[
				"chatHistory",
				(payload?: any) => {
					if (cancelled) return;

					const raw = asRecord(payload);
					if (typeof raw.roomId === "string" && raw.roomId !== roomId) return;

					const history = toChatMessages(raw.messages);
					// A no-op, never a clear: `getRoomChat` answers `[]` when Redis is down.
					if (history.length === 0) return;

					setById(new Map(history.map((message) => [message.id, message])));
				},
			],
			[
				"chatMessageReceived",
				(payload?: any) => {
					if (cancelled) return;

					const raw = asRecord(payload);
					if (typeof raw.roomId === "string" && raw.roomId !== roomId) return;

					const message = toChatMessage(raw.message);
					if (!message) return;

					setById((prev) => {
						if (prev.has(message.id)) return prev;

						const next = new Map(prev);
						next.set(message.id, message);
						return next;
					});

					const self = selfRoleRef.current;
					if (!self || message.senderRole !== self) setUnreadCount((count) => count + 1);
				},
			],
			[
				"chatMessageRejected",
				(payload?: any) => {
					if (cancelled) return;

					const raw = asRecord(payload);
					if (typeof raw.roomId === "string" && raw.roomId !== roomId) return;

					setError(messageForRejection(raw.reason));
				},
			],
			[
				"authError",
				(payload?: any) => {
					if (cancelled) return;

					const event = asRecord(payload).event;
					if (event !== "chatMessage" && event !== "requestChatHistory") return;

					// Reset rather than retry here: the next `roomInfo` is the one that means the join landed.
					historyRequestedRef.current = false;
					setError(NOT_AUTHORISED);
				},
			],
		];

		const attach = () => {
			if (cancelled) return;

			const socket = getSocketRef.current();
			if (!socket) {
				attempts += 1;
				if (attempts > SOCKET_LOOKUP_ATTEMPTS) return;

				lookupTimer = setTimeout(attach, SOCKET_LOOKUP_INTERVAL_MS);
				return;
			}

			attached = socket;
			socketRef.current = socket;
			handlers.forEach(([event, handler]) => socket.on(event, handler));

			setConnected(!!socket.connected);
			// Already connected ⇒ the page connected (and therefore joined) before this panel mounted.
			if (socket.connected) requestHistory();
		};

		attach();

		return () => {
			cancelled = true;
			if (lookupTimer) clearTimeout(lookupTimer);
			// By reference, never a blanket `off(event)` — this socket is a singleton shared with the page.
			if (attached) handlers.forEach(([event, handler]) => attached?.off(event, handler));
			socketRef.current = null;
			historyRequestedRef.current = false;
		};
	}, [enabled, roomId]);

	const sendMessage = useCallback(
		(content: string): boolean => {
			const trimmed = typeof content === "string" ? content.trim() : "";

			if (!trimmed) {
				setError(REJECTION_MESSAGES.content_empty);
				return false;
			}
			if (trimmed.length > CHAT_CONTENT_MAX_LENGTH) {
				setError(REJECTION_MESSAGES.content_too_long);
				return false;
			}

			const socket = socketRef.current;
			if (!socket || !socket.connected) {
				setError(NOT_CONNECTED);
				return false;
			}

			setError(null);
			// `id`, `timestamp`, `senderRole` and `senderUserId` are the server's — it ignores any we send.
			socket.emit("chatMessage", { roomId, content: trimmed, type: "text", attachments: [], senderName: senderNameRef.current ?? null });
			return true;
		},
		[roomId],
	);

	const sendAttachment = useCallback(
		async (file: File): Promise<boolean> => {
			const upload = uploadRef.current;
			if (!upload) {
				setError(ATTACHMENTS_UNAVAILABLE);
				return false;
			}
			if (!socketRef.current || !socketRef.current.connected) {
				setError(NOT_CONNECTED);
				return false;
			}

			setError(null);

			let attachments: ConsultChatAttachment[];
			try {
				attachments = await upload(file);
			} catch (uploadError) {
				// ⛔ Nothing is emitted, and nothing is logged — an axios error here carries the consultation link.
				setError(messageForUploadError(uploadError));
				return false;
			}

			if (attachments.length === 0) {
				setError(GENERIC_ATTACHMENT_ERROR);
				return false;
			}

			const socket = socketRef.current;
			if (!socket || !socket.connected) {
				setError(NOT_CONNECTED);
				return false;
			}

			socket.emit("chatMessage", { roomId, content: "", type: "file", attachments, senderName: senderNameRef.current ?? null });
			return true;
		},
		[roomId],
	);

	const markRead = useCallback(() => setUnreadCount(0), []);
	const dismissError = useCallback(() => setError(null), []);

	// Stable sort over a Map's insertion order, so messages sharing a timestamp keep arrival order.
	const messages = useMemo(() => Array.from(byId.values()).sort((a, b) => a.timestamp - b.timestamp), [byId]);

	return { messages, sendMessage, sendAttachment, unreadCount, markRead, isReady: enabled && connected, error, dismissError };
};
